// InvitationAcceptance.jsx - Component for accepting tenant invitations
// Add this as a new file: src/shared/components/InvitationAcceptance.jsx

import React, { useState } from 'react';
import { TenantService } from '../../platform/services/TenantService';
import { Building2, Mail, CheckCircle } from 'lucide-react';

/**
 * InvitationAcceptance - Handles tenant admin invitation acceptance
 */
export default function InvitationAcceptance({ user, invitation, onAccepted }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [accepted, setAccepted] = useState(false);

  const handleAcceptInvitation = async () => {
    setLoading(true);
    setError(null);

    try {
      console.log('✅ Accepting invitation for:', invitation.tenant.name);
      
      const userProfile = await TenantService.acceptTenantInvitation(invitation.id, {
        uid: user.uid,
        email: user.email,
        name: user.displayName || ''
      });

      setAccepted(true);
      
      // Notify parent component
      setTimeout(() => {
        onAccepted(userProfile);
      }, 2000);

    } catch (err) {
      console.error('❌ Failed to accept invitation:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (accepted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-50 flex items-center justify-center">
        <div className="max-w-md w-full mx-4">
          <div className="bg-white rounded-lg shadow-xl p-8 text-center">
            <CheckCircle className="mx-auto text-green-500 mb-4" size={64} />
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Welcome Aboard!</h1>
            <p className="text-gray-600 mb-4">
              You've successfully joined <strong>{invitation.tenant.name}</strong> as an administrator.
            </p>
            <p className="text-sm text-gray-500">
              Redirecting to your company dashboard...
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
      <div className="max-w-md w-full mx-4">
        <div className="bg-white rounded-lg shadow-xl p-8">
          {/* Header */}
          <div className="text-center mb-6">
            <Building2 className="mx-auto text-blue-600 mb-4" size={48} />
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Company Invitation</h1>
            <p className="text-gray-600">You've been invited to join as an administrator</p>
          </div>

          {/* Invitation Details */}
          <div className="bg-blue-50 rounded-lg p-4 mb-6">
            <div className="flex items-start gap-3">
              <Mail className="text-blue-600 mt-1" size={20} />
              <div className="flex-1">
                <h3 className="font-semibold text-blue-900">{invitation.tenant.name}</h3>
                <p className="text-sm text-blue-700">{invitation.tenant.industry || 'Business'}</p>
                <p className="text-xs text-blue-600 mt-1">
                  Invited by: {invitation.invitedBy}
                </p>
              </div>
            </div>
          </div>

          {/* User Info */}
          <div className="mb-6">
            <h4 className="font-medium text-gray-900 mb-2">Your Account</h4>
            <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-sm text-gray-700">
                <strong>Email:</strong> {user.email}
              </p>
              {user.displayName && (
                <p className="text-sm text-gray-700">
                  <strong>Name:</strong> {user.displayName}
                </p>
              )}
            </div>
          </div>

          {/* Permissions Info */}
          <div className="bg-amber-50 rounded-lg p-4 mb-6">
            <h4 className="font-medium text-amber-900 mb-2">Administrator Access</h4>
            <ul className="text-sm text-amber-800 space-y-1">
              <li>• Full access to company dashboard</li>
              <li>• Manage company settings and features</li>
              <li>• View and manage company data</li>
              <li>• Invite and manage team members</li>
            </ul>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
              <p className="text-red-800 text-sm">{error}</p>
            </div>
          )}

          {/* Action Buttons */}
          <div className="space-y-3">
            <button
              onClick={handleAcceptInvitation}
              disabled={loading}
              className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Accepting Invitation...
                </>
              ) : (
                <>
                  <CheckCircle size={16} />
                  Accept & Join Company
                </>
              )}
            </button>
            
            <p className="text-xs text-gray-500 text-center">
              By accepting, you agree to become an administrator for {invitation.tenant.name}
            </p>
          </div>

          {/* Invitation Details */}
          <div className="mt-6 pt-4 border-t border-gray-200 text-center">
            <p className="text-xs text-gray-400">
              Invitation expires: {new Date(invitation.expiresAt).toLocaleDateString()}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}